import React, { useEffect, useState } from "react"
import api from "../../../api2"
import ICamper from "../../../interfaces/ICamper"
import { useDebouncedCallback } from "use-debounce"
import { tSortDirection } from "../../../api2/camps"
import Camper from "./Camper"
import { FormProvider } from "../../../contexts/FormContext"
import PaginatedTable from "./PaginatedTable"
import { useCamper } from "../Reservations/ReservationHooks/useCamper"
import { useNavigate, useParams } from "react-router-dom"
import { ROUTER_PATHS } from "~/routes/constants"
import { useCampContext } from "~/contexts/CampProvider"

const Campers = () => {
  const { campId } = useCampContext()
  const navigate = useNavigate()
  const params = useParams()
  const guestId = params.guestId
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(50)
  const [campers, setCampers] = useState<ICamper[]>([])
  const [totalCampers, setTotalCampers] = useState<number>()
  const [totalPages, setTotalPages] = useState<number>()
  const [isLoading, setIsLoading] = useState(false)
  const [sortBy, setSortBy] = useState(null)
  const [isAscending, setIsAscending] = useState(false)
  const currentPage = localStorage.getItem("toNav")
  const [selectedCamperId, setSelectedCamperId] = useState<string | null>()
  const { data } = useCamper({ camperId: selectedCamperId })
  const selectedCamper = data?.camper

  useEffect(() => {
    const guestId = params.guestId
    if (guestId) setSelectedCamperId(guestId)
  }, [params])

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === "selectedCamper") {
        setSelectedCamperId(e.newValue)
      }
    }

    window.addEventListener("storage", handler)
    return () => window.removeEventListener("storage", handler)
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [selectedCamperId])

  const handleManipulateSelectedCamper = (camper: ICamper) => {
    setSelectedCamperId(camper?.id || null)
    if (camper?.id) navigate(`${ROUTER_PATHS.ADMIN_GUESTS}/${camper?.id}`)

    if (camper && camper?.id !== undefined) localStorage.setItem("selectedCamperId", camper?.id)
    else localStorage.removeItem("selectedCamperId")
  }

  const fetchCampers = useDebouncedCallback((callback?: () => void) => {
    const paginationOptions = {
      page: page,
      per_page: perPage,
      sort_column: sortBy,
      sort_direction: (isAscending ? "asc" : "desc") as tSortDirection,
    }

    api.Camps.fetchCampers(campId, paginationOptions)
      .then(({ campers: _campers, total_pages, total_records }) => {
        setCampers(_campers)
        setTotalCampers(total_records)
        setTotalPages(total_pages)
        setIsLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setIsLoading(false)
      })
      .finally(() => callback?.())
  }, 50)

  useEffect(() => {
    setPage(1)
  }, [sortBy, isAscending])

  useEffect(() => {
    if(campId) {
      setIsLoading(true)
      fetchCampers()
    }
    if(selectedCamper  && campId && selectedCamper?.camp_id !== campId) {
      setSelectedCamperId(null)
      navigate(`${ROUTER_PATHS.ADMIN_GUESTS}`)
    }
  }, [campId, page, perPage, sortBy, isAscending, selectedCamper])

  useEffect(() => {
    if (!selectedCamper) return

    setCampers(campers.map((c) => (c.id === selectedCamper.id ? selectedCamper : c)))
  }, [selectedCamper])

  useEffect(() => {
    if (currentPage !== "guests" && selectedCamper) {
      handleManipulateSelectedCamper(null)
    }
  }, [currentPage])

  useEffect(() => {
    if (guestId && selectedCamper) {
      const name = [selectedCamper.first_name, selectedCamper.last_name].filter(Boolean).join(" ") || selectedCamper.email || "Guest"
      document.title = `${name} - Guests - Park Admin`
    } else {
      document.title = "Guests - Park Admin"
    }
  }, [guestId, selectedCamper])

  return (
    <div className="w-full lg:mx-auto mx-4">
      {!guestId ? (
        <div className="">
          <PaginatedTable
            reloadCampers={fetchCampers}
            records={campers}
            isLoading={isLoading}
            selectedRecord={selectedCamper}
            totalRecords={totalCampers}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            perPage={perPage}
            setPerPage={setPerPage}
            sortBy={sortBy}
            setSortBy={setSortBy}
            isAscending={isAscending}
            setIsAscending={setIsAscending}
            onRowClick={(camper: ICamper) => handleManipulateSelectedCamper(camper)}
          />
        </div>
      ) : (
        <div className="">
          <FormProvider
            init={{
              ...selectedCamper,
              notes: selectedCamper?.notes || "",
              reservations: selectedCamper?.reservations || [],
            }}
          >
            <Camper
              camper={selectedCamper}
              setSelectedCamper={handleManipulateSelectedCamper}
              reloadCampers={fetchCampers}
            />
          </FormProvider>
        </div>
      )}
    </div>
  )
}

export default Campers
